import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaSitemap }        from '@react-icons/all-files/fa/FaSitemap';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog }            from '@react-icons/all-files/fa/FaCog';
import { FaHome }           from '@react-icons/all-files/fa/FaHome';
import { FaPlus }           from '@react-icons/all-files/fa/FaPlus';
import { FaCheckCircle }    from '@react-icons/all-files/fa/FaCheckCircle';
import { FaTimesCircle }    from '@react-icons/all-files/fa/FaTimesCircle';
import Breadcrumb    from '../../../../commons/Breadcrumb';
import PageHeader    from '../../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../../commons/PageAlert';
import Confirm, { useConfirm }     from '../../../../commons/Confirm';
import { EditButton, DeleteButton, DesignButton, RenderActions } from '../../../../commons/DataGridActions';
import ProfaseDataGrid from '../../../../commons/Datagrid';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntities, deleteEntity, reset } from '../../../../manager/workflow-def.reducer';
import { formatWorkflowPeriodicidadLabel } from '../../../../shared/workflow-periodicidad';

const BASE = '/configuracion/workflow/definicion-workflow';

const boolCell = (params: any) =>
  params.value
    ? <FaCheckCircle style={{ color: '#16a34a', fontSize: 15 }} />
    : <FaTimesCircle style={{ color: '#dc2626', fontSize: 15 }} />;

const objCell = (params: any) =>
  params.value?.nombre ?? params.value?.id ?? '—';

export default function WorkflowDefPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const topRef   = useRef<HTMLDivElement>(null);

  const entities      = useAppSelector(s => s.workflowDef.entities);
  const totalItems    = useAppSelector(s => s.workflowDef.totalItems);
  const loading       = useAppSelector(s => s.workflowDef.loading);
  const updating      = useAppSelector(s => s.workflowDef.updating);
  const updateSuccess = useAppSelector(s => s.workflowDef.updateSuccess);

  const [rows, setRows]             = useState<any[]>([]);
  const [lastParams, setLastParams] = useState<any>(null);

  const { alert, showToast }                               = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  useEffect(() => {
    const msg = (location.state as any)?.successMsg;
    if (msg) { showToast('success', msg); window.history.replaceState({}, ''); }
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      dismiss();
      if (lastParams) dispatch(getEntities(lastParams));
      showToast('success', 'Definición de workflow eliminada correctamente.');
    }
  }, [updateSuccess]);

  useEffect(() => { setConfirmLoading(updating); }, [updating]);
  useEffect(() => { setRows(entities ?? []); }, [entities]);

  const fetchData = (params: any) => {
    setLastParams(params);
    dispatch(getEntities(params));
  };

  const renderActions = (params: any) => (
    <RenderActions>
      <DesignButton onClick={() => navigate(`${BASE}/${params.row.id}/diseno`)} />
      <EditButton   onClick={() => navigate(`${BASE}/${params.row.id}/edit`)} />
      <DeleteButton onClick={() => {
        ask(
          <>¿Confirmas que deseas eliminar <strong>{params.row.nombre}</strong>? Esta acción no se puede deshacer.</>,
          () => dispatch(deleteEntity(params.row.id)),
          { title: 'Eliminar Definición Workflow', confirmLabel: 'Eliminar', variant: 'danger' },
        );
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }} />
    </RenderActions>
  );

  const cols = [
    { field: 'id',            headerName: 'ID',             flex: 0.4 },
    { field: 'nombre',        headerName: 'Nombre',         flex: 1.5 },
    { field: 'periodicidad',  headerName: 'Periodicidad',   flex: 1,
      renderCell: (p: any) => formatWorkflowPeriodicidadLabel(p.value) },
    { field: 'periodo',       headerName: 'Período',        flex: 0.8 },
    { field: 'porDefecto',    headerName: 'Por Defecto',    flex: 0.7, renderCell: boolCell },
    { field: 'executorClass', headerName: 'Executor Class', flex: 1.5 },
    { field: 'workflowClase', headerName: 'Clase',          flex: 0.8, renderCell: objCell },
    { field: 'nodoInicio',    headerName: 'Nodo Inicio',    flex: 0.8, renderCell: objCell },
    { field: 'plantilla',     headerName: 'Plantilla',      flex: 0.8,
      renderCell: (p: any) => p.value?.nombre ?? <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Sin plantilla</span> },
    { field: '__actions',     headerName: '',               flex: 1.0, renderCell: renderActions },
  ];

  const filters = [
    { label: 'Nombre',        name: 'nombre' },
    { label: 'Periodicidad',  name: 'periodicidad' },
    { label: 'Executor Class', name: 'executorClass' },
  ];

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb items={[
        { label: 'Inicio',              to: '/',                       icon: <FaHome /> },
        { label: 'Configuración',       to: '/configuracion',          icon: <FaCog /> },
        { label: 'Workflow',            to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
        { label: 'Definición Workflow',                                icon: <FaSitemap /> },
      ]} />

      <PageHeader
        icon={<FaSitemap />}
        iconColor="#3b82f6"
        title="Definición Workflow"
        subtitle="Gestión de definiciones de workflow"
        action={{
          label: <><FaPlus style={{ marginRight: 6 }} />Nueva Definición</>,
          onClick: () => navigate(`${BASE}/new`),
          color: '#3b82f6',
        }}
      />

      <Confirm config={confirmConfig} />
      {alert && <PageAlert alert={alert} />}

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={filters}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
      />
    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
};
